import { useRef, useCallback, useEffect } from 'react'
import { EventTrackingEventType, trackEvent } from 'lib/eventTracking'
import { useAppSelector } from '@redux/hooks'
import { HandsOnTableBinder as UntypedHandsOnTableBinder } from 'handsontable/binder/HandsOnTableBinder'

import { useRowHeightObserver } from 'handsontable/renderers/useRowHeightObserver'
import type Handsontable from 'handsontable/base'
import type Core from 'handsontable/core'

interface IHookArgs {
  getHandsontableInstance: () => Handsontable | null
  getHotTableContainerRef: () => HTMLElement | null
  binderOptions: Record<string, unknown>
  dataContainer: { templateFields?: unknown[] }
  setParentComponentRef: (node: HandsOnTableBinder) => void
  updateSettings: (
    bind: HandsOnTableBinder,
    handsontableInstance: Handsontable | null,
    hotTableContainer: HTMLElement | null,
  ) => object
  isReady: boolean
  afterBindCallback: () => void
}

interface IListGovernance {
  list_column?: {
    powered_template_field_id
  }
}

interface TemplateField {
  field?: {
    governance_type?: string
  }
  governance_type?: string
  list_governance?: IListGovernance
}

interface HandsOnTableBinder {
  visibleRenderColumnsOrderById: Record<string, number>
  visibleRenderColumns: { field_id: string; governance_type?: string }[]
  allRenderColumns: unknown
  allRenderColumnsByFieldId: unknown
  resyncMeta: () => void
}

// Converts JS class to typed constructor to avoid linting errors
const HotTableBinder = UntypedHandsOnTableBinder as unknown as new (config: {
  dataContainer: unknown
  table: unknown
  options: Record<string, unknown>
}) => HandsOnTableBinder

/**
 * Initialize the Handsontable Binder class when HandsontableInstance is ready.
 * - Sets Handsontable updateSettings.
 * - Runs a few side-effects; set ref in parent container to access instance;
 * trigger callback on completion.
 */
export const useHandsontableBinder = ({
  getHandsontableInstance,
  getHotTableContainerRef,
  binderOptions,
  dataContainer,
  setParentComponentRef,
  updateSettings,
  isReady,
  afterBindCallback,
}: IHookArgs) => {
  const featureChipsUILists = useAppSelector(state => state.Auth.features['ui-chips-list']) ?? false
  const binderInstanceRef = useRef<unknown>(null)
  const handsontableInstance = getHandsontableInstance()
  const { setRowHeightObserver } = useRowHeightObserver({ table: handsontableInstance as Core })

  const setHandsOnTableBinder = useCallback((table: Handsontable) => {
    const bind = new HotTableBinder({
      dataContainer,
      table,
      options: {
        ...binderOptions,
        setRowHeightObserver,
        featureChipsUILists,
      },
    })

    bind.visibleRenderColumnsOrderById = bind.visibleRenderColumns.reduce(
      (memo: Record<string, number>, col: { field_id: string }, index: number) => {
        memo[col.field_id] = index
        return memo
      },
      {},
    )

    const containerRef = getHotTableContainerRef()
    const settings = updateSettings(bind, table, containerRef)
    table.updateSettings(settings)

    bind.resyncMeta()

    afterBindCallback()

    setParentComponentRef(bind)

    binderInstanceRef.current = bind
  }, [])

  useEffect(() => {
    if (!isReady) return

    const tableBinderStart = performance.now()
    trackEvent(EventTrackingEventType.HOT_TABLE_LOAD_START)

    if (handsontableInstance) {
      setHandsOnTableBinder(handsontableInstance)
    }

    const templateFields = dataContainer.templateFields
    const tableBinderEnd = performance.now()
    if (Array.isArray(templateFields)) {
      trackEvent(
        EventTrackingEventType.HOT_TABLE_LOAD_COMPLETE,
        createLoadEventProperties(templateFields, tableBinderStart, tableBinderEnd),
      )
    }
  }, [isReady])
}

function createLoadEventProperties(
  templateFields: unknown[],
  tableBinderStart: number,
  tableBinderEnd: number,
) {
  const typedTemplateFields = templateFields as TemplateField[]
  const listFields = typedTemplateFields
    .filter(tf => (tf.field?.governance_type ?? tf.governance_type) == 'list')
    .map(tf => tf.list_governance) as (IListGovernance | null)[]

  return {
    hasList: Boolean(listFields.length),
    hasPoweredList: listFields.some(field => field?.list_column?.powered_template_field_id),
    timing: Math.round(tableBinderEnd - tableBinderStart),
  }
}
